import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Stack, LinearProgress, Tooltip, Chip, List, ListItemButton,
  ListItemIcon, ListItemText, Grid, Button, alpha,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useCanOpen } from '../../context/PageAccessContext';

// ── Formatting ───────────────────────────────────────────────────────────────
export const compact = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e7) return `${(v / 1e7).toFixed(1).replace(/\.0$/, '')}Cr`;
  if (Math.abs(v) >= 1e5) return `${(v / 1e5).toFixed(1).replace(/\.0$/, '')}L`;
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(1).replace(/\.0$/, '')}K`;
  return v.toLocaleString('en-IN');
};
export const rupees = (n) => `₹${compact(n)}`;
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '—');

export const CONTRACT_STATUS = {
  draft:                  { label: 'Draft', color: 'default' },
  active:                 { label: 'Active', color: 'default' },
  sent_for_review:        { label: 'Sent for review', color: 'warning' },
  user_requested_changes: { label: 'Changes requested', color: 'warning' },
  user_agreed:            { label: 'Awaiting signatures', color: 'info' },
  signed:                 { label: 'Signed', color: 'success' },
  terminated:             { label: 'Ended', color: 'default' },
};
export const StatusChip = ({ status }) => {
  const cfg = CONTRACT_STATUS[status] || { label: status, color: 'default' };
  return <Chip size="small" label={cfg.label} color={cfg.color} variant={cfg.color === 'default' ? 'outlined' : 'filled'} />;
};

// ── Page header ──────────────────────────────────────────────────────────────
export function DashboardHeader({ title, subtitle, chip }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-end' }} spacing={1} mb={3}>
      <Box>
        <Typography variant="h4" fontWeight={800}>{title}</Typography>
        {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
      </Box>
      {chip}
    </Stack>
  );
}

// ── Stat tile: label · value · optional sub-line; clickable when `to` is given ─
export function StatTile({ label, value, sub, to: target, icon, hero = false }) {
  const navigate = useNavigate();
  const canOpen = useCanOpen();
  const to = target && canOpen(target) ? target : null;
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%', cursor: to ? 'pointer' : 'default', transition: 'border-color .15s',
        '&:hover': to ? { borderColor: 'primary.main' } : undefined,
      }}
      onClick={to ? () => navigate(to) : undefined}
      role={to ? 'link' : undefined}
    >
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          {icon && <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>}
        </Stack>
        <Typography fontWeight={700} sx={{ fontSize: hero ? 48 : 30, lineHeight: 1.15, mt: 0.5 }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

export function KpiRow({ children }) {
  return (
    <Grid container spacing={2} mb={3}>
      {React.Children.toArray(children).filter(Boolean).map((child, i) => (
        <Grid item xs={6} md={3} key={i}>{child}</Grid>
      ))}
    </Grid>
  );
}

// ── Meter: a single ratio against its total ──────────────────────────────────
export function Meter({ label, value, total, caption }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <Box mb={1.5}>
      <Stack direction="row" justifyContent="space-between" mb={0.5}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" fontWeight={600}>{caption ?? `${value} of ${total}`}</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={Math.min(pct, 100)} sx={{ height: 8, borderRadius: 4 }} aria-label={`${label}: ${pct}%`} />
    </Box>
  );
}

// ── Section card ─────────────────────────────────────────────────────────────
export function Section({ title, action, children }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography variant="h6" fontWeight={700}>{title}</Typography>
          {action}
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

// ── Horizontal bars (one series, one hue). Optional second part `rest` is shown
//    de-emphasised in grey inside the same bar (emphasis form), with a legend. ──
export function BarList({ data, valueLabel = 'count', restLabel, emptyText = 'Nothing to show yet.' }) {
  if (!data?.length) return <Typography variant="body2" color="text.secondary">{emptyText}</Typography>;
  const max = Math.max(...data.map(d => d.value + (d.rest || 0)), 1);
  return (
    <Box>
      {restLabel && (
        <Stack direction="row" spacing={2} mb={1.5}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: 'primary.main' }} />
            <Typography variant="caption" color="text.secondary">{valueLabel}</Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: 'grey.400' }} />
            <Typography variant="caption" color="text.secondary">{restLabel}</Typography>
          </Stack>
        </Stack>
      )}
      <Stack spacing={1}>
        {data.map(d => {
          const total = d.value + (d.rest || 0);
          const tip = restLabel ? `${d.label}: ${d.value} ${valueLabel.toLowerCase()}, ${d.rest || 0} ${restLabel.toLowerCase()}` : `${d.label}: ${d.value}`;
          return (
            <Tooltip key={d.label} title={tip} placement="top" followCursor>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(90px, 38%) 1fr auto', alignItems: 'center', gap: 1.5, py: 0.25 }}>
                <Typography variant="body2" noWrap title={d.label}>{d.label}</Typography>
                <Box sx={{ display: 'flex', gap: '2px', height: 12 }}>
                  {d.value > 0 && <Box sx={{ width: `${(d.value / max) * 100}%`, bgcolor: 'primary.main', borderRadius: '0 4px 4px 0', minWidth: 4 }} />}
                  {d.rest > 0 && <Box sx={{ width: `${(d.rest / max) * 100}%`, bgcolor: 'grey.400', borderRadius: '0 4px 4px 0', minWidth: 4 }} />}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 24, textAlign: 'right' }}>{total}</Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Stack>
    </Box>
  );
}

// ── "Needs your attention" list: each item links to where it's resolved ─────
export function AttentionList({ items: allItems, emptyText = "You're all caught up.", limit = 6 }) {
  const navigate = useNavigate();
  const canOpen = useCanOpen();
  const [expanded, setExpanded] = React.useState(false);
  const items = (allItems || []).map(i => (i.link && !canOpen(i.link) ? { ...i, link: null } : i));
  if (!items.length) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'success.main' }}>
        <CheckCircleIcon fontSize="small" />
        <Typography variant="body2">{emptyText}</Typography>
      </Stack>
    );
  }
  const shown = expanded ? items : items.slice(0, limit);
  return (
    <>
    <List dense disablePadding>
      {shown.map((item, i) => (
        <ListItemButton
          key={i}
          onClick={() => item.link && navigate(item.link)}
          sx={{ borderRadius: 1, mb: 0.5, bgcolor: (t) => alpha(item.waiting ? t.palette.info.main : t.palette.warning.main, 0.08) }}
        >
          <ListItemIcon sx={{ minWidth: 34 }}>
            {item.waiting ? <HourglassEmptyIcon fontSize="small" color="info" /> : <WarningAmberIcon fontSize="small" color="warning" />}
          </ListItemIcon>
          <ListItemText primary={item.text} secondary={item.secondary} primaryTypographyProps={{ variant: 'body2' }} />
          {item.link && <ChevronRightIcon fontSize="small" color="action" />}
        </ListItemButton>
      ))}
    </List>
    {items.length > limit && (
      <Button size="small" onClick={() => setExpanded(e => !e)}>
        {expanded ? 'Show fewer' : `Show all ${items.length}`}
      </Button>
    )}
    </>
  );
}

// Small text button to another page; hidden when the user can't open that page
export function LinkButton({ to, children }) {
  const navigate = useNavigate();
  const canOpen = useCanOpen();
  if (!canOpen(to)) return null;
  return <Button size="small" onClick={() => navigate(to)}>{children}</Button>;
}

// ── Simple table-like list of rows ───────────────────────────────────────────
export function RowList({ rows, empty = 'Nothing here yet.' }) {
  if (!rows?.length) return <Typography variant="body2" color="text.secondary">{empty}</Typography>;
  return (
    <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
      {rows.map((r, i) => (
        <Stack key={i} direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="div" variant="body2" fontWeight={600} noWrap>{r.primary}</Typography>
            {r.secondary && <Typography component="div" variant="caption" color="text.secondary" noWrap>{r.secondary}</Typography>}
          </Box>
          {r.right}
        </Stack>
      ))}
    </Stack>
  );
}
